import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Header,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Textarea,
  TextContent,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationRedriveAttempt,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useCancelDeletion,
  useExecuteDeletion,
  useRequestDeletion,
} from "../../hooks/useInvestigations";
import { CopyToClipboard } from "../common/CopyToClipboard";
import { formatTimestamp } from "./investigationUtils";
import { SafeExternalLink } from "./SafeExternalLink";

interface InvestigationDeletionFlowProps {
  stage: Stage;
  investigation: Investigation;
  /** Attempts already loaded by the panel (the pending DELETE attempt is looked up here). */
  attempts: InvestigationRedriveAttempt[];
  isOwner: boolean;
  onError: (message: string) => void;
  /**
   * Controlled request dialog: when set, the flow renders no "Delete without redrive…" button of
   * its own (the Redrive options modal opens the dialog instead).
   */
  requestOpen?: boolean;
  onRequestOpenChange?: (open: boolean) => void;
}

/** MCM statuses in which the redriver accepts the deletion. */
const EXECUTABLE_MCM_STATUSES = new Set(["In Progress"]);
const DEAD_MCM_STATUSES = new Set([
  "Cancelled",
  "Discarded",
  "Rejected",
  "Aborted",
]);

export const mcmStatusIndicator = (status?: string | null) => {
  if (!status) {
    return (
      <StatusIndicator type="pending">MCM status unavailable</StatusIndicator>
    );
  }
  if (EXECUTABLE_MCM_STATUSES.has(status)) {
    return <StatusIndicator type="success">MCM {status}</StatusIndicator>;
  }
  if (DEAD_MCM_STATUSES.has(status)) {
    return <StatusIndicator type="error">MCM {status}</StatusIndicator>;
  }
  return <StatusIndicator type="in-progress">MCM {status}</StatusIndicator>;
};

/** The "Delete without redrive…" entry point shown beside the redrive button. */
export const RequestDeletionButton: React.FC<{
  isOwner: boolean;
  isReady: boolean;
  onClick: () => void;
}> = ({ isOwner, isReady, onClick }) => (
  <Button
    data-testid="request-deletion"
    disabled={!isOwner || !isReady}
    disabledReason={
      !isOwner
        ? "Only the owner can request a deletion."
        : !isReady
        ? "The investigation must be Ready to redrive first."
        : undefined
    }
    onClick={onClick}
  >
    Delete without redrive…
  </Button>
);

/**
 * "Delete without redrive" for an investigation. Mirrors the standalone DLQ redrive tool's
 * approval flow (Step 1: create and approve the MCM, Step 2: execute after approval), bound to the
 * frozen manifest. While the request is pending the case stays READY_TO_REDRIVE with
 * `pendingDeletion` set, which locks refresh/redrive/status changes server-side.
 *
 * Renders the request button when nothing is pending, or the two-step containers when it is.
 */
export const InvestigationDeletionFlow: React.FC<
  InvestigationDeletionFlowProps
> = ({
  stage,
  investigation,
  attempts,
  isOwner,
  onError,
  requestOpen: controlledOpen,
  onRequestOpenChange,
}) => {
  const request = useRequestDeletion(stage);
  const execute = useExecuteDeletion(stage);
  const cancel = useCancelDeletion(stage);
  const [localOpen, setLocalOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const requestOpen = controlled ? controlledOpen : localOpen;
  const setRequestOpen = (open: boolean) =>
    controlled ? onRequestOpenChange?.(open) : setLocalOpen(open);
  const [confirmExecute, setConfirmExecute] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [justification, setJustification] = useState("");
  const [simLink, setSimLink] = useState("");

  const pending = investigation.pendingDeletion;
  // A rule case's launches are prepared on the worker first; while any attempt is PREPARING the
  // case carries pendingLaunch (a deletion request shows that instead of pendingDeletion) and no
  // other launch may start (409 LAUNCH_PENDING), so the button waits like Redrive and Merge do.
  const preparing = !!investigation.pendingLaunch;
  const preparingDeletion = investigation.pendingLaunch?.kind === "DELETE";
  const pendingAttempt = pending
    ? attempts.find((a) => a.attemptId === pending.attemptId)
    : undefined;
  const status = investigation.status ?? "";
  const isReady = status === "READY_TO_REDRIVE";
  const running =
    pendingAttempt?.status === "STARTED" ||
    pendingAttempt?.status === "RUNNING";
  const failed = pendingAttempt?.status === "FAILED";
  const canExecute = isOwner && isReady && !!pending && !running;
  const canCancel = isOwner && !!pending && !running;

  const fail = (err: InvestigationApiError) => {
    if (err.code === "MANIFEST_DRIFTED") {
      onError(
        `${err.message}${
          err.driftedDlqPks?.length
            ? ` (e.g. ${err.driftedDlqPks.slice(0, 3).join(", ")})`
            : ""
        }`
      );
      return;
    }
    onError(err.message);
  };

  const submitRequest = () =>
    request
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        justification: justification.trim(),
        simLink: simLink.trim() || undefined,
      })
      .then(() => {
        setRequestOpen(false);
        setJustification("");
        setSimLink("");
      })
      .catch(fail);

  const submitExecute = () =>
    execute
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        attemptId: pending!.attemptId!,
      })
      .then(() => setConfirmExecute(false))
      .catch(fail);

  const submitCancel = () =>
    cancel
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
        attemptId: pending!.attemptId!,
      })
      .then(() => setConfirmCancel(false))
      .catch(fail);

  const modals = (
    <>
      <Modal
        visible={requestOpen}
        onDismiss={() => setRequestOpen(false)}
        header="Delete without redrive"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setRequestOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="submit-deletion-request"
                variant="primary"
                disabled={!justification.trim()}
                loading={request.isLoading}
                onClick={() => {
                  submitRequest().catch(console.error);
                }}
              >
                Create Draft MCM
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="s">
          <TextContent>
            <p>
              This freezes the exact DLQ-PK manifest of the{" "}
              {investigation.assignedCount ?? 0} items this investigation
              currently owns, uploads it to the DLQ redrive bucket and creates a
              Draft MCM bound to that file. Nothing is deleted until the MCM is
              approved and you execute it here. Until then the population is
              locked.
            </p>
          </TextContent>
          <FormField
            label="Justification"
            description="Why these items are deleted instead of fixed and redriven. Included in the MCM description for the approver."
          >
            <Textarea
              data-testid="deletion-justification"
              value={justification}
              onChange={({ detail }) => setJustification(detail.value)}
              rows={4}
            />
          </FormField>
          <FormField label="SIM link (optional)">
            <Input
              value={simLink}
              onChange={({ detail }) => setSimLink(detail.value)}
              placeholder="https://issues.amazon.com/issues/TRACK-12345"
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={confirmExecute}
        onDismiss={() => setConfirmExecute(false)}
        header="Execute deletion?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmExecute(false)}>
                Cancel
              </Button>
              <Button
                data-testid="confirm-execute-deletion"
                variant="primary"
                loading={execute.isLoading}
                onClick={() => {
                  submitExecute().catch(console.error);
                }}
              >
                Execute deletion
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          <p>
            This permanently deletes {pending?.requestedCount ?? 0} DLQ items
            under MCM <strong>{pending?.mcmId}</strong> without redriving them.
            The run only proceeds if the MCM is In Progress and its description
            still matches the frozen file. The investigation moves to Executing
            and Lusca verifies the items are gone in the next hourly snapshot.
          </p>
        </TextContent>
      </Modal>

      <Modal
        visible={confirmCancel}
        onDismiss={() => setConfirmCancel(false)}
        header="Discard deletion request?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirmCancel(false)}>
                Keep request
              </Button>
              <Button
                data-testid="confirm-cancel-deletion"
                variant="primary"
                loading={cancel.isLoading}
                onClick={() => {
                  submitCancel().catch(console.error);
                }}
              >
                Discard request
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <TextContent>
          <p>
            The request is withdrawn in Lusca and the population is unlocked.
            MCM <strong>{pending?.mcmId}</strong> is not cancelled
            automatically; discard it in MCM so it cannot be used later.
          </p>
        </TextContent>
      </Modal>
    </>
  );

  if (!pending) {
    if (controlled) return modals;
    if (preparing) {
      return (
        <>
          <StatusIndicator type="loading">
            {preparingDeletion
              ? "Preparing the deletion request (listing the items from the latest DLQ snapshot)"
              : "Another attempt is being prepared; delete without redrive once it has started"}
          </StatusIndicator>
          {modals}
        </>
      );
    }
    return (
      <>
        <RequestDeletionButton
          isOwner={isOwner}
          isReady={isReady}
          onClick={() => setRequestOpen(true)}
        />
        {modals}
      </>
    );
  }

  return (
    <>
      <Container
        data-testid="pending-deletion"
        header={
          <Header
            variant="h3"
            description={`${pending.requestedCount ?? 0} items frozen by ${
              pending.requestedBy ?? "unknown"
            } ${formatTimestamp(
              pending.requestedAt
            )}. The population is locked (no refresh, redrive, status change or split) until this request is executed or discarded.`}
          >
            Delete without redrive
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Container
            header={<Box variant="h3">Step 1: Create and approve MCM</Box>}
          >
            <SpaceBetween size="s">
              <Alert type="success" header="Draft MCM created">
                <SpaceBetween size="xs">
                  <Box>
                    <SafeExternalLink
                      href={pending.mcmUrl}
                      externalIconAriaLabel="Opens MCM in a new tab"
                    >
                      Open {pending.mcmId}
                    </SafeExternalLink>
                  </Box>
                  <Box>
                    In MCM, send for approval and then return here to execute
                    deletion
                  </Box>
                  {!running && mcmStatusIndicator(pendingAttempt?.mcmStatus)}
                </SpaceBetween>
              </Alert>
              {pendingAttempt?.mcmWarning && (
                <Alert type="warning" header="MCM verification incomplete">
                  {pendingAttempt.mcmWarning}
                </Alert>
              )}
              <ExpandableSection headerText="Request details">
                <SpaceBetween size="s">
                  {pendingAttempt?.justification && (
                    <FormField label="Justification">
                      <TextContent>
                        <p style={{ margin: 0 }}>
                          {pendingAttempt.justification}
                        </p>
                      </TextContent>
                    </FormField>
                  )}
                  {pendingAttempt?.redriveS3Uri && (
                    <FormField
                      label="S3 URI"
                      secondaryControl={
                        <CopyToClipboard
                          getContent={() =>
                            Promise.resolve(pendingAttempt.redriveS3Uri ?? "")
                          }
                          iconName="copy"
                          ariaLabel="Copy S3 URI"
                        />
                      }
                    >
                      <Input value={pendingAttempt.redriveS3Uri} readOnly />
                    </FormField>
                  )}
                  {pendingAttempt?.redriveS3Etag && (
                    <FormField
                      label="ETag"
                      secondaryControl={
                        <CopyToClipboard
                          getContent={() =>
                            Promise.resolve(pendingAttempt.redriveS3Etag ?? "")
                          }
                          iconName="copy"
                          ariaLabel="Copy ETag"
                        />
                      }
                    >
                      <Input value={pendingAttempt.redriveS3Etag} readOnly />
                    </FormField>
                  )}
                </SpaceBetween>
              </ExpandableSection>
            </SpaceBetween>
          </Container>

          <Container
            header={<Box variant="h3">Step 2: Execute after MCM approval</Box>}
          >
            <SpaceBetween size="m">
              {running ? (
                <Alert type="info" header="Deletion running">
                  <StatusIndicator type="loading">
                    Lusca is deleting the frozen items and will move the
                    investigation on when the run completes.
                  </StatusIndicator>
                </Alert>
              ) : (
                <Alert type="info" header="Approval happens in MCM">
                  Before executing, confirm the MCM is approved and{" "}
                  <b>In Progress</b>. This execution reuses the prepared file
                  above; the redriver validates the MCM (status, approver, title
                  and the exact S3 URI + ETag literal) before deleting anything.
                </Alert>
              )}
              {failed && (
                <Alert type="error" header="Last run failed">
                  {pendingAttempt?.error ||
                    "The deletion did not complete. Check the MCM and execute again, or discard the request."}
                </Alert>
              )}
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  data-testid="execute-deletion"
                  variant="primary"
                  disabled={!canExecute}
                  disabledReason={
                    !isOwner
                      ? "Only the owner can execute the deletion."
                      : running
                      ? "The deletion is running."
                      : undefined
                  }
                  onClick={() => setConfirmExecute(true)}
                >
                  {failed ? "Execute deletion again" : "Execute deletion"}
                </Button>
                {canCancel && (
                  <Button
                    data-testid="cancel-deletion"
                    onClick={() => setConfirmCancel(true)}
                  >
                    Discard
                  </Button>
                )}
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        </SpaceBetween>
      </Container>
      {modals}
    </>
  );
};

export default InvestigationDeletionFlow;
